import { useEffect } from "react";

const GOOGLE_FONTS_BASE_URL = "https://fonts.googleapis.com/css2";

/**
 * Generates the Google Fonts URL for a given font family.
 * Returns null for system fonts or undefined.
 */
const getGoogleFontUrl = (fontFamily: string | undefined): string | null => {
  if (!fontFamily || fontFamily === "system") return null;

  // URL encode the font name (spaces become +)
  const encodedFont = fontFamily.replace(/ /g, "+");

  // Request multiple weights for flexibility
  return `${GOOGLE_FONTS_BASE_URL}?family=${encodedFont}:wght@400;500;600;700&display=swap`;
};

/**
 * Injects a font stylesheet link into the document head.
 * Returns true if a new link was added, false if it already existed.
 */
const injectFontLink = (url: string, identifier: string): boolean => {
  const existingLink = document.querySelector(`link[data-font-id="${identifier}"]`);
  if (existingLink) return false;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.setAttribute("data-font-id", identifier);
  document.head.appendChild(link);
  return true;
};

/**
 * Hook that dynamically loads Google Fonts.
 * Supports both curated font family names and custom Google Fonts CSS URLs.
 * Also loads optional per-text-style fonts (headingFont, bodyFont).
 *
 * @param fontFamily - The font family name to load (or "system" for default)
 * @param customFontUrl - Optional custom Google Fonts CSS URL
 * @param headingFont - Optional font family for headings (H1–H4)
 * @param bodyFont - Optional font family for body/paragraph text
 */
export const useFontLoader = (
  fontFamily: string | undefined,
  customFontUrl: string | undefined,
  headingFont?: string | undefined,
  bodyFont?: string | undefined
): void => {
  useEffect(() => {
    // If custom URL is provided, use it directly
    if (customFontUrl) {
      injectFontLink(customFontUrl, `custom-${customFontUrl}`);
      return;
    }

    // Otherwise, generate URL from font family name
    const fontUrl = getGoogleFontUrl(fontFamily);
    if (fontUrl) {
      injectFontLink(fontUrl, `font-${fontFamily}`);
    }
  }, [fontFamily, customFontUrl]);

  useEffect(() => {
    const headingUrl = getGoogleFontUrl(headingFont);
    if (headingUrl) {
      injectFontLink(headingUrl, `font-${headingFont}`);
    }
  }, [headingFont]);

  useEffect(() => {
    const bodyUrl = getGoogleFontUrl(bodyFont);
    if (bodyUrl) {
      injectFontLink(bodyUrl, `font-${bodyFont}`);
    }
  }, [bodyFont]);
};

/**
 * Parses a Google Fonts URL to extract the font family name.
 * Handles URLs like:
 * - https://fonts.googleapis.com/css2?family=Playwrite+AT:ital,wght@0,100..400
 * - https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700
 * - https://fonts.googleapis.com/css2?family=Roboto
 *
 * @param url - The Google Fonts CSS URL
 * @returns The font family name, or null if parsing fails
 */
export const parseFontFamilyFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const familyParam = urlObj.searchParams.get("family");
    if (!familyParam) return null;

    // Take the first font family (before any `:` for weight/style options)
    // and replace `+` with spaces
    const fontName = (familyParam.split(":")[0] ?? familyParam).replace(/\+/g, " ");
    return fontName || null;
  } catch {
    return null;
  }
};

/**
 * Preloads all fonts from the curated list.
 * Useful for rendering font previews in dropdowns.
 */
export const preloadCuratedFonts = (): void => {
  const fonts = [
    "Inter",
    "Roboto",
    "Open Sans",
    "Lato",
    "Poppins",
    "Montserrat",
    "Nunito",
    "Source Sans Pro",
    "Raleway",
    "Playfair Display",
    "Merriweather",
  ];

  fonts.forEach((font) => {
    const url = getGoogleFontUrl(font);
    if (url) {
      injectFontLink(url, `font-${font}`);
    }
  });
};
